// =============================================================================
// Table Component — Generic typed data table
// =============================================================================

import { clsx } from 'clsx';

export interface Column<T> {
  key: string;
  /** Header cell content (string or a custom node, e.g. a select-all box). */
  header: React.ReactNode;
  /** Custom cell renderer */
  render?: (row: T, index: number) => React.ReactNode;
  /** Tailwind classes for the column */
  className?: string;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  /** Unique key extractor */
  rowKey: (row: T, index: number) => string | number;
  emptyMessage?: string;
  className?: string;
  loading?: boolean;
  onRowClick?: (row: T, index: number) => void;
}

export function Table<T>({
  columns,
  data,
  rowKey,
  emptyMessage = 'No data to display',
  className,
  loading = false,
  onRowClick,
}: TableProps<T>) {
  return (
    <div className={clsx('overflow-x-auto rounded-lg border border-border', className)}>
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-secondary">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary',
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-surface">
          {loading ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-sm text-text-tertiary"
              >
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-brand-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Loading...
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-sm text-text-tertiary"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={rowKey(row, idx)}
                onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                className={clsx(
                  'transition-colors duration-100',
                  onRowClick && 'cursor-pointer hover:bg-surface-secondary',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={clsx(
                      'px-4 py-3 text-sm text-text-primary whitespace-nowrap',
                      col.className,
                    )}
                  >
                    {col.render
                      ? col.render(row, idx)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

Table.displayName = 'Table';
